import { useEffect } from "react";
import { create } from "zustand";
import type { PresenceViewer } from "@breeze/shared";

const HEARTBEAT_MS = 25_000;

type PresenceStore = {
  // cardId -> viewers (array of { email, color })
  byCard: Record<number, PresenceViewer[]>;
  set: (cardId: number, viewers: PresenceViewer[]) => void;
};

export const usePresenceStore = create<PresenceStore>((set) => ({
  byCard: {},
  set: (cardId, viewers) =>
    set((s) => ({ byCard: { ...s.byCard, [cardId]: viewers } })),
}));

async function hit(path: string, cardId: number): Promise<void> {
  await fetch(`/api/presence/${path}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cardId }),
  }).catch(() => undefined);
}

// Start emitting heartbeats for the given card. Sends a leave on unmount or
// cardId change. Call with `null` to be inert.
export function useCardPresence(cardId: number | null): void {
  useEffect(() => {
    if (cardId === null) return;
    void hit("heartbeat", cardId);
    const id = window.setInterval(() => void hit("heartbeat", cardId), HEARTBEAT_MS);
    const onUnload = (): void => void hit("leave", cardId);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("beforeunload", onUnload);
      void hit("leave", cardId);
    };
  }, [cardId]);
}
